import { useAppConfig } from "~/app/config/useAppConfig";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { useCallback, useMemo, useState } from "react";
import { isKITProduct, type KITProductData, type ProductData } from "~/domains/product/types/ProductData";
import Parameter from "~/common/components/Parameter";

import CustomNumberFormat, { type NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import { useDispatch } from "react-redux";
import {
  addToCartThunk,
  fetchWishDetailThunk,
  removeProductThunk,
  updateCommentItemThunk,
  updatePriceRefThunk,
  updateProductThunk,
  updateQuantityThunk,
} from "~/features/wishlist/redux/wishlistSlice";
import { sendWishlistProductClick } from "~/app/analytics/ga/ga";
import { userIsCustomer, userIsRegistered } from "~/domains/user/utils/roles";
import { Permission, userHasAnyPermission } from "~/domains/user/utils/permissions";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import {
  type ExpandedResolvedWishlistEntryModel,
  type ResolvedWishlistEntryModel,
} from "~/features/wishlist/types/model";
import { Card } from "~/features/configurator/Linear/components/Card";
import {
  getSurchargeValueFromTotalPrice,
  getTariff,
  getTotalPrice,
  isProductPMA,
} from "~/domains/product/utils/productDescriptorUtils";
import { getIsContractAvailable } from "~/features/productDetails/redux/productDetailsSelectors";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { Accordion, AccordionDetails, AccordionSummary, Button, IconButton, Stack, styled } from "~/common/mui";
import Checkbox from "~/common/components/Checkbox";
import ProductImage from "~/domains/product/components/ProductImage";
import { Translation } from "~/app/i18n/Translation";
import { PredefinedTraits } from "~/features/productDetails/components/ProductTrait";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "~/features/compareProducts/components/ProductComparisonCheckbox";
import { WishlistMaterialIdInput } from "~/pages/WishlistPage/components/WishlistDetails/components/WishlistItem/WishlistMaterialIdInput";
import { MAX_QUANTITY_FORMAT_LENGTH } from "~/utils/constants";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { type Row } from "@tanstack/react-table";
import { useIsMaxTabletSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { UndoButton } from "~/common/components/Button/UndoButton";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { LinkButton, LinkType } from "~/common/components/Link";
import { isPriceReferenceParamVisible } from "~/features/cart/cart/components/LineItemStaticParams";
import {
  IncludedPartsTable,
  OptionalPartsTable,
} from "~/pages/WishlistPage/components/WishlistDetails/components/WishlistItem/PartTables";
import { useTranslation } from "react-i18next";
import { IncludedSurcharge } from "~/domains/product/components/ProductParams";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import {
  calculateSalesCampaign4PDP,
  productHasAvailableCampaign,
  useSelectPricingDetails,
} from "~/features/salesCampaign/utils/salesCampaignUtils";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { CommentButton } from "~/common/components/CommentButton/CommentButton";
import { Alert } from "~/common/mui/Alert/Alert";
import DeleteIcon from "@mui/icons-material/Delete";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import ProductMMD from "~/domains/product/components/ProductMMD";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { PriceElements, type PriceProps } from "~/domains/pricing/components/Price/PriceV4";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { SaleQuantityIndicator } from "~/domains/salesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AutorenewIcon from "@mui/icons-material/Autorenew";

interface Props {
  index: number;
  row: Row<ExpandedResolvedWishlistEntryModel>;
  entryId: string;
  canAddToCart?: boolean;
  setSaleEnabled?: (saleEnabled: boolean) => void;
}

const WIDTHS_MOBILE = {
  product: "124px",
  gap: "32px",
  brand: "30px",
  manufacturer: "27px",
};
const WIDTHS = {
  product: "100px",
  gap: "16px",
  brand: "30px",
  manufacturer: "27px",
};

const Root = styled(Card, {
  shouldForwardProp: (prop) => prop !== "$noPriceRef",
})<{ $noPriceRef: boolean }>(({ theme }) => ({
  minWidth: 0,
  width: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("tablet")]: {
    display: "grid",
    gridTemplateColumns: "24px 180px 200px 1fr",
    columnGap: theme.spacing(4),
    rowGap: theme.spacing(4),
  },
  [theme.breakpoints.up("desktop-s")]: {
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(2),
    gridTemplateColumns: "24px minmax(120px, 148px) 180px 196px 86px 196px minmax(200px, 1fr)",
  },
  [theme.breakpoints.up("desktop-m")]: {
    gridTemplateColumns: "40px minmax(120px, 148px) 228px 228px 102px 228px minmax(240px, 1fr)",
  },
  [theme.breakpoints.up("desktop-l")]: {
    columnGap: theme.spacing(4),
    gridTemplateColumns: "40px minmax(120px, 148px) 272px 272px 120px 272px minmax(260px, 1fr)",
  },
}));

const MobileHeaderSection = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(6),
  [theme.breakpoints.up("tablet")]: {
    display: "contents",
  },
}));

const ActionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  marginTop: -12,
  marginRight: -12,
  gap: theme.spacing(4),
  alignItems: "center",
  [theme.breakpoints.up("tablet")]: {
    gridColumn: "3 / -1",
    gridRow: "1 / 2",
    justifyContent: "flex-end",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "-4 / -1",
    gap: 0,
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  fontSize: 24,
  padding: theme.spacing(1),
  [theme.breakpoints.up("desktop-s")]: {
    fontSize: 16,
  },
}));

const StyledIcon = styled(DeleteIcon)(({ theme }) => ({
  fontSize: "24px !important",
  [theme.breakpoints.up("desktop-s")]: {
    fontSize: 16,
  },
}));

const ImageWrapper = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up("tablet")]: {
    gridColumn: "2 / 3",
    gridRow: "1 / 3",
    margin: 0,
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridRow: "2 / 3",
  },
}));

const DesignationWrapper = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("tablet")]: {
    gridColumn: "3 / 4",
    gridRow: "2 / 3",
    marginBottom: 0,
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "4 / 5",
  },
}));

const Description = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(6),
  [theme.breakpoints.up("tablet")]: {
    margin: 0,
    gridColumn: "2 / 3",
    gridRow: "3 / 5",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "3 / 4",
    gridRow: "2 / 4",
  },
}));

const ProductComparison = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
  [theme.breakpoints.up("tablet")]: {
    marginTop: theme.spacing(2),
  },
}));

const QuantityWrapper = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up("tablet")]: {
    gridColumn: "4 / 5",
    gridRow: "2 / 3",
    marginBottom: 0,
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "5 / 6",
  },
}));

const PriceReference = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up("tablet")]: {
    gridColumn: "3 / 4",
    gridRow: "3 / 4",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "6 / 7",
    gridRow: "2 / 3",
    width: "100%",
  },
}));

const PriceBlock = styled("div")(({ theme }) => ({
  minWidth: 0,
  marginBottom: theme.spacing(6),
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  columnGap: theme.spacing(4),
  [theme.breakpoints.up("tablet")]: {
    gridColumn: "4 / 5",
    gridRow: "3 / 4",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "var(--start-col-desktop, 7) / 8",
    gridRow: "2 / 3",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
}));

const AddToCartButton = styled(Button)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("tablet")]: {
    marginBottom: 0,
    width: "auto",
    justifySelf: "flex-end",
    alignSelf: "flex-end",
    gridColumn: "4 / 5",
    gridRow: "6 / 7",
  },
  [theme.breakpoints.up("desktop-s")]: {
    marginTop: theme.spacing(2),
    gridColumn: "-4 / -1",
    gridRow: "4 / 6",
    alignSelf: "flex-end",
  },
}));

const Messages = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  "&:empty": {
    display: "none",
    marginBottom: 0,
  },
  [theme.breakpoints.up("tablet")]: {
    marginBottom: 0,
    gridColumn: "1 / -1",
    gridRow: "6 / 7",
    maxWidth: "75%",
  },
  [theme.breakpoints.up("desktop-s")]: {
    maxWidth: "unset",
    gridColumn: "1 / -4",
    gridRow: "5 / 6",
  },
}));

const WishlistCommentInput = styled(CommentInput)(({ theme }) => ({
  minWidth: 0,
  width: "100%",
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "4 / -1",
    gridRow: "3 / 4",
  },
}));

const TabletBottomBlock = styled("div")(({ theme }) => ({
  gridColumn: "1 / -1",
  gridRow: "5 / 6",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
  position: "relative",
}));

const KitSectionRoot = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  [theme.breakpoints.up("tablet")]: {
    gridRow: "6 / 7",
    gridColumn: "1 / -1",
    marginBottom: 0,
  },
}));

const KitSectionDetails = styled(AccordionDetails)(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: `calc(${theme.spacing(6)} - 11px)`,
  paddingBottom: 0,
  gap: theme.spacing(6),
  "& td, & th": {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    [theme.breakpoints.up("tablet")]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },
  "& th": {
    fontWeight: "normal",
    color: theme.palette.text.secondary,
  },
}));

export const WishlistItem = ({ index, canAddToCart, row, setSaleEnabled }: Props) => {
  const { t } = useTranslation();
  const { selection } = usePriceReferenceSelection();
  const isMinTablet = useIsMinTabletSize();
  const isMaxTablet = useIsMaxTabletSize();

  const isTablet = isMinTablet && isMaxTablet;

  const wishlistEntry = row.original;
  const productDescriptor = wishlistEntry.product;
  const productData = productDescriptor.base;
  const isContractProduct = getIsContractAvailable(productDescriptor);

  const packCode = wishlistEntry.packCode;

  const saleEnabled: boolean = wishlistEntry.isSaleEnabled;

  const salesHelper = useSalesHelper(productDescriptor);
  const isOnSale = salesHelper.isPackageOnSale(packCode);
  const saleQuantity = salesHelper.forPackage(packCode)?.stock ?? 0;

  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor }), [productDescriptor]);
  const { netPrice, price, oldPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity: wishlistEntry.quantity,
    priceReference: wishlistEntry.priceReference,
    productCode: productData.code,
    salesData: data?.salePerPack?.[packCode],
    saleActivated: saleEnabled,
  });
  // TODO - This needs to be fixed, price should be able to be used by itself
  const total = getTotalPrice(
    price
      ? {
          ...netPrice,
          ...price,
          baseValue: price.value,
          unit: netPrice?.unit ?? 1,
          surcharge: netPrice?.surcharge ?? 0,
        }
      : null,
    wishlistEntry.quantity,
  );

  const hasSales = productHasAvailableCampaign(productData.salesCampaign);

  const dispatch = useDispatch();
  const { onUpdateProduct, onUpdateQty, onUpdatePriceRef, onAddToCart, onUpdateCommentItem, onDeleteItem, onFetchPnA } =
    useMemo(
      () => ({
        onUpdateProduct: (newProduct: ProductData) => dispatch(updateProductThunk(row.original.id, newProduct)),
        onUpdateQty: (newQty?: number) => dispatch(updateQuantityThunk(row.original.id, newQty ?? 0)),
        onUpdatePriceRef: (newPriceRef?: string) => dispatch(updatePriceRefThunk(row.original.id, newPriceRef!)),
        onUpdateCommentItem: (comment: string) => dispatch(updateCommentItemThunk(row.original.id, comment)),
        onDeleteItem: () => dispatch(removeProductThunk(row.original.id)),
        onAddToCart: () => dispatch(addToCartThunk(row.original.id)),
        onFetchPnA: (code: string, priceReference: string) => dispatch(fetchWishDetailThunk(code, priceReference)),
      }),
      [dispatch, row.original.id],
    );

  const isLoaded = productDescriptor.isLoaded;
  const isLoading = productDescriptor.isLoading;

  const updateQty = (e: NumberFormatEvent) => onUpdateQty(e.floatValue);

  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;

  let disableAddToCart;
  if (userIsCustomer()) {
    disableAddToCart = !canAddToCart || !isLoaded || wishlistEntry.quantity === undefined;
  } else {
    disableAddToCart = !canAddToCart || wishlistEntry.quantity === undefined;
  }
  disableAddToCart = disableAddToCart || webservicesDisabled;

  const isRegistered = userIsRegistered();
  const canSeePrice = userHasAnyPermission(Permission.ADD_TO_QUOTE, Permission.ADD_TO_CART) && !isRegistered;

  const showLoadPnALink = canSeePrice && !isLoaded;

  const canSeeComment = canUseCommentsForProduct(productData.marketType);

  const visibleIndex = index + 1;

  const addToCartCb = useCallback(() => {
    sendWishlistProductClick(productData, "Select", visibleIndex);
    onAddToCart();
  }, [onAddToCart, productData, visibleIndex]);

  const productDetailsCb = useCallback(() => {
    sendWishlistProductClick(productData, "Product information", visibleIndex);
  }, [productData, visibleIndex]);

  const isCustomer = userIsCustomer();

  const successorProduct = productDescriptor.base.successorProduct;
  const [previousProduct, setPreviousProduct] = useState<ProductData | null>(null);

  const [commentRef, toggleComment] = useShowAndScrollInput(row.toggleExpanded);
  const hasComment = !!wishlistEntry.comment;
  const actions = (
    <ActionContainer>
      {canSeeComment && <CommentButton onClick={toggleComment} open={row.getIsExpanded()} fontSize={"small"} />}
      <IconButton color={"primary"} onClick={onDeleteItem} data-test-id={"wishlist-item-delete"}>
        <StyledIcon />
      </IconButton>
      <StyledCheckbox
        disabled={!row.getCanSelect()}
        checked={row.getIsSelected()}
        data-test-id={"wishlist-item-cbox-toggle"}
        onCheck={row.getToggleSelectedHandler()}
      />
    </ActionContainer>
  );

  const image = (
    <ProductImage product={productData} widths={isMinTablet ? WIDTHS : WIDTHS_MOBILE} onClick={productDetailsCb} />
  );

  const designationInput = (
    <DesignationWrapper>
      <WishlistMaterialIdInput
        onChange={(p) => {
          onUpdateProduct(p);
          setPreviousProduct(null);
        }}
        value={productData.designation}
        isPreferred={productData.preferred}
        disabled={isKITProduct(productDescriptor)}
      />
    </DesignationWrapper>
  );

  let descriptionBlock;
  if (isMinTablet) {
    descriptionBlock = (
      <Description>
        {productDescriptor.base.marketDesignation && (
          <ProductMMD marketDesignation={productDescriptor.base.marketDesignation} />
        )}
        <div>
          <Translation {...productData.shortDescription} />
        </div>
        {!isRegistered && !isProductPMA(productDescriptor) && (
          <Parameter label={t("product.schaefflerId")} value={productData.sapMaterialNumber} />
        )}
        {isContractProduct && <PredefinedTraits.ContractProduct className={"mt-2"} />}
        {isProductComparisonCheckboxVisible(productDescriptor.base) && (
          <ProductComparison>
            <ProductComparisonCheckbox productCode={productDescriptor.base.code} gaLocation={"Wishlist page"} />
          </ProductComparison>
        )}
      </Description>
    );
  } else {
    descriptionBlock = (
      <Description>
        <div>
          <Translation {...productData.shortDescription} />
        </div>
        {isContractProduct && <PredefinedTraits.ContractProduct className={"mt-2"} />}
        {isProductComparisonCheckboxVisible(productDescriptor.base) && (
          <ProductComparison>
            <ProductComparisonCheckbox productCode={productDescriptor.base.code} gaLocation={"Wishlist page"} />
          </ProductComparison>
        )}
      </Description>
    );
  }

  const quantityInput = (
    <QuantityWrapper>
      {productData.quantityVisible ? (
        <CustomNumberFormat
          label={t("input.quantity")}
          data-test-id={"wishlist-item-input-quantity"}
          value={wishlistEntry.quantity}
          handleState={updateQty}
          maxLength={MAX_QUANTITY_FORMAT_LENGTH}
        />
      ) : (
        <Parameter label={t("input.quantity")} value={1} />
      )}
      {isOnSale && saleEnabled && <SaleQuantityIndicator.V2 quantity={saleQuantity} variant="labelSmall" />}
    </QuantityWrapper>
  );

  const priceReferenceInput = isCustomer && (
    <PriceReference gap={1} data-testid="wishlist-price-reference">
      <PriceReferenceParam
        product={productData}
        priceReference={wishlistEntry.priceReference}
        onChange={onUpdatePriceRef}
        data-test-id={"package-entry-price-reference"}
        editable={canSeePriceRefColumn(selection)}
        disabled={saleEnabled}
        size="medium"
      />
      <CheckboxWithLabel
        checked={saleEnabled}
        onCheck={setSaleEnabled}
        hidden={!isOnSale || !!wishlistEntry.priceReference}
      >
        {t("feature.salesCampaign.specialDealPrice")}
      </CheckboxWithLabel>
    </PriceReference>
  );

  let priceBlock = null;
  if (!isLoaded && isLoading) {
    priceBlock = <CircleSpinner />;
  }
  if (isLoaded) {
    const surchargeValue = getSurchargeValueFromTotalPrice(total);
    const hasSurcharge = (netPrice?.surcharge ?? 0) > 0;
    const tariffSurcharge = isTariffSurchargeEnabled() && getTariff(row.original.product);

    priceBlock = (
      <PriceBlock data-testid="wishlist-price-block">
        <PriceElements.Stack>
          <PriceElements.NetPriceLabel price={netPrice} />
          <Stack direction="row" gap={1}>
            <CurrentPrice zeroText={t("product.priceOnRequest")} price={price} surchargeEnabled={hasSurcharge} />
            <SaleDiscountIndicator.V2 discountFormatted={discountFormatted} />
          </Stack>
          {oldPrice && <OldPrice price={oldPrice} variant="bodySmall" />}
        </PriceElements.Stack>
        <div>
          <PriceElements.Stack>
            <PriceElements.TotalPriceLabel price={netPrice} />
            <CurrentPrice price={total} fontWeight={"bold"} />
          </PriceElements.Stack>
          {hasSurcharge && (
            <IncludedSurcharge className={"mt-3"} value={surchargeValue} percent={netPrice?.surcharge ?? 0} />
          )}
          {tariffSurcharge && <p className="label-small">{t("price.tariff.surcharge.applicable")}</p>}
        </div>
      </PriceBlock>
    );
  }
  if (isLoaded && netPrice && netPrice.value <= 0 && !isProductPMA(productDescriptor)) {
    priceBlock = <PriceBlock data-testid="wishlist-price-block">{t("product.priceOnRequest")}</PriceBlock>;
  }

  let housingBlock = null;
  if (isKITProduct(productDescriptor)) {
    housingBlock = <KITSection product={productDescriptor} entry={wishlistEntry} />;
  }

  const cta = canAddToCart && (
    <AddToCartButton
      data-test-id={"wishlist-item-add-to-cart"}
      size={"medium"}
      onClick={addToCartCb}
      startIcon={<ShoppingCartIcon />}
      disabled={disableAddToCart}
    >
      {t("wishlistPage.button.add")}
    </AddToCartButton>
  );

  const messagesBlock = (
    <Messages>
      {NewVersionMessage.isVisible(productDescriptor.base) && (
        <NewVersionMessage
          baseProduct={productDescriptor.base}
          onClick={() => {
            setPreviousProduct(productDescriptor.base);
            onUpdateProduct(successorProduct!);
            dispatch(
              ProductService.getProductDescriptor(successorProduct!.code, wishlistEntry.priceReference, {
                fetch: true,
                forceFetchBasicData: true,
              }),
            );
          }}
          disableLink={true}
        />
      )}
      {previousProduct && (
        <UndoButton
          onClick={() => {
            setPreviousProduct(null);
            onUpdateProduct(previousProduct);
          }}
        />
      )}
      {!wishlistEntry.available && <Alert severity={"error"}>{t("wishlist.productUnavailable")}</Alert>}
      {showLoadPnALink && (
        <LinkButton
          variant={LinkType.PRIMARY}
          disabled={isLoading}
          data-test-id={"btn-load-pna"}
          onClick={(event) => {
            event.preventDefault();
            onFetchPnA(productData.code, wishlistEntry.priceReference);
          }}
          iconBefore={<AutorenewIcon />}
        >
          {t("page.cart.table.loadPna")}
        </LinkButton>
      )}
      {isProductPMA(productDescriptor) && userIsCustomer() && <InfoProductMessagePMA />}
    </Messages>
  );

  let commentBlock = null;
  if (row.getIsExpanded()) {
    commentBlock = (
      <WishlistCommentInput
        label={t("wishlistPage.comment.label")}
        value={wishlistEntry.comment}
        onChange={onUpdateCommentItem}
        ref={commentRef}
      />
    );
  }

  return (
    <Root
      disableHover={true}
      $noPriceRef={!isPriceReferenceParamVisible(selection)}
      sx={{
        "--start-col-desktop": !isPriceReferenceParamVisible(selection) ? 6 : 7,
      }}
    >
      <MobileHeaderSection>{actions}</MobileHeaderSection>

      {hasSales && (
        <SaleTag
          sx={{
            position: "absolute",
            top: 16,
            left: 32,
          }}
          size={40}
        />
      )}
      <ImageWrapper>{image}</ImageWrapper>
      {designationInput}
      {descriptionBlock}
      {priceReferenceInput}
      {quantityInput}
      {priceBlock}
      {!isTablet && (
        <>
          {housingBlock}
          {commentBlock}
          {messagesBlock}
          {cta}
        </>
      )}
      {isTablet && (
        <TabletBottomBlock>
          {commentBlock}
          {housingBlock}
          {messagesBlock}
          {cta}
        </TabletBottomBlock>
      )}
    </Root>
  );
};

interface KITSectionProps {
  className?: string;
  product: ProductDescriptor;
  entry: ResolvedWishlistEntryModel;
}
const KITSection = ({ className, product, entry }: KITSectionProps) => {
  const { t } = useTranslation();
  const tableProps = {
    product: product.base as KITProductData,
    quantity: entry.quantity,
    priceReference: entry.priceReference,
  };
  return (
    <KitSectionRoot>
      <AccordionSummary>{t("page.wishlist.section.housing.title")}</AccordionSummary>
      <KitSectionDetails>
        <IncludedPartsTable {...tableProps} />
        <OptionalPartsTable wishlistOptionalParts={entry.optionalParts} {...tableProps} />
      </KitSectionDetails>
    </KitSectionRoot>
  );
};

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.BodySmall),
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
